// @flow
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { useSectionedModal } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';

import useIntl from '../../../../../hooks/useIntl';
import useResidentQuery from '../../../../../hooks/data-fetching/useResidentQuery';
import usePetTypeOptionsQuery, {
  DEFAULT_OPTION_VALUE,
} from './usePetTypeOptionsQuery';
import useHouseholdPetMutation from './useHouseholdPetMutation';
import useHardDeletePetMutation from './useHardDeletePetMutation';
// eslint-disable-next-line max-len
import useCreateApplicantHouseholdMemberMutation from '../../../../../hooks/data-modifying/useCreateApplicantHouseholdMemberMutation';
import { getExistingApplicantAdults } from 'containers/ResidentProfile/MiniProfileBridge/buildSavePayload';
import {
  usePetCharges,
  usePetFeeModal,
} from '../../../../EditResidentHousehold/PetFeeModal/hooks';
import { invalidateOccupantInfoQueries } from '../../../../ResidentProfile/MiniProfileBridge';
import { notifyMissingTransactionTypes } from '../../../../../utils/missingTransactionTypesNotice';
import { extractErrorMessage } from '../../../../../utils/errors';
import {
  buildPetFields,
  SERVICE_ANIMAL_NO,
  SERVICE_ANIMAL_YES,
} from '../fields';
import {
  buildAddPetPayload,
  buildAddPetWithFeesPayload,
  buildApplicantPetPayload,
  buildUpdateResidentPetPayload,
} from '../buildSavePayload';
import messages from '../messages';
import appMessages from '../../../../App/messages';
import type { PetTypeOption, PetViewModel, SelectedFee } from '../../types';

export const PET_SECTION_ID = 'pet';

/** Converts an API timestamp to a timezone-independent calendar date. */
const toDatePickerValue = (value: ?string): string =>
  value ? formatDateDB(value) : '';

/** Builds defaults with explicit unanswered select values. */
export const buildDefaultValues = (pet: ?PetViewModel): Object => {
  if (!pet) {
    return {
      name: '',
      petTypeId: DEFAULT_OPTION_VALUE,
      petBreedId: DEFAULT_OPTION_VALUE,
      weight: '',
      petTag: '',
      isServiceAnimal: DEFAULT_OPTION_VALUE,
      startDate: '',
      endDate: '',
    };
  }

  return {
    name: pet.name ?? '',
    petTypeId: pet.petTypeId ?? DEFAULT_OPTION_VALUE,
    petBreedId: pet.petBreedId ?? DEFAULT_OPTION_VALUE,
    weight: pet.weight ?? '',
    petTag: pet.petTag ?? '',
    isServiceAnimal: pet.isServiceAnimal
      ? SERVICE_ANIMAL_YES
      : SERVICE_ANIMAL_NO,
    startDate: toDatePickerValue(pet.startDate),
    endDate: toDatePickerValue(pet.endDate),
  };
};

/** Returns positive fee counts keyed by property fee ID. */
export const buildSelectedFees = (feeValues: ?Object): Array<SelectedFee> =>
  Object.entries(feeValues ?? {})
    .map(([propertyFeeId, count]) => ({
      propertyFeeId,
      count: Number(count) || 0,
    }))
    .filter(({ count }) => count > 0);

const findPetTypeOption = (
  petTypeOptions: Array<PetTypeOption>,
  petTypeId: ?string,
): ?PetTypeOption =>
  petTypeOptions.find((option) => option.value === petTypeId);

const usePetModal = ({
  organizationId,
  propertyId,
  residentId,
  applicationId,
  applicants,
  isApplicantSubject,
  pet,
  hasOpenCerts,
  canDelete,
  onClose,
  onSaveSuccess,
}: {
  organizationId: string,
  propertyId: string,
  residentId: ?string,
  applicationId: ?string,
  applicants: ?Array<Object>,
  isApplicantSubject: boolean,
  pet: ?PetViewModel,
  hasOpenCerts: boolean,
  canDelete: boolean,
  onClose: () => void,
  onSaveSuccess: ?() => void,
}) => {
  const intl = useIntl();
  const formatMessage = intl.formatMessage;
  const queryClient = useQueryClient();
  const { automatedLeaseChargesP1 } = useFlags();

  const isNewPet = !pet;
  // Current and prior pets use the resident-pet update operation.
  const isResidentPet = pet?.kind === 'resident' || pet?.kind === 'prior';
  // Pending pets belong to an applicant record this modal cannot write to, so
  // they are read-only here no matter what else the household allows.
  const isUnsupportedPet = pet?.kind === 'pending';
  // An open certification freezes the household composition it is certifying,
  // so no pet may be added, edited, or ended until that cert is completed or
  // cancelled -- the rule the legacy Edit Household page and the member Mini
  // Profile both enforce. The server enforces none of it, so this is the
  // only gate.
  const isLockedByOpenCerts = Boolean(hasOpenCerts);
  // Denied pets cannot open this modal.
  const areFieldsDisabled = isLockedByOpenCerts || isUnsupportedPet;
  // Word-for-word the string the Mini Profile shows on its locked fields.
  const openCertHelperText = formatMessage(appMessages.editHouseholdBlocked);

  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const { petTypeOptions, isLoading: isLoadingPetTypes } =
    usePetTypeOptionsQuery({ chooseLabel: formatMessage(messages.choose) });

  // A pet cannot start before the household moved in -- the same rule the
  // member Mini Profile enforces on its own Start/End Date fields, read from
  // the same resident query (React Query dedupes it with the profile's own
  // read, so this costs no extra request).
  const { data: residentQueryData, isLoading: isLoadingResident } =
    useResidentQuery({ organizationId, propertyId, residentId });
  const householdMoveInDay =
    toDatePickerValue(residentQueryData?.householdInfo?.householdMoveInDate) ||
    null;
  // The backend creates the pet rent charge from this lease field, so the
  // modal has to read it too. It rides on the resident query already made
  // above, so it costs no extra request. Kept nullish rather than coerced to
  // 0: while the query is still loading, or if the response carries no lease,
  // the amount is unknown, and the modal must not claim it is $0.00.
  const leaseMonthlyPetRent =
    residentQueryData?.residentInfo?.lease?.monthlyPetRent ?? null;

  const defaultValues = useMemo(() => buildDefaultValues(pet), [pet]);

  // Keep the section identity stable while fields respond to pet-type changes.
  const baseSections = useMemo(
    () => [
      {
        id: PET_SECTION_ID,
        navigationLabel: formatMessage(messages.navigationLabel),
        sectionTitle: formatMessage(messages.sectionTitle),
        fields: [],
      },
    ],
    [formatMessage],
  );

  const { ReactHookFormProps, sections: resolvedSections } = useSectionedModal({
    sections: baseSections,
    defaultValues,
  });

  const { watch, setValue, getValues, trigger } = ReactHookFormProps;
  const petTypeId = watch('petTypeId');

  const selectedPetTypeOption = findPetTypeOption(petTypeOptions, petTypeId);
  const requiresWeight = Boolean(selectedPetTypeOption?.requiresWeight);
  const breedOptions = useMemo(
    () =>
      selectedPetTypeOption?.petBreeds ?? [
        {
          value: DEFAULT_OPTION_VALUE,
          text: formatMessage(messages.choose),
          disabled: true,
        },
      ],
    [selectedPetTypeOption, formatMessage],
  );

  // Clear Breed only when Pet Type changes after initialization.
  const previousPetTypeIdRef = useRef(defaultValues.petTypeId);
  useEffect(() => {
    if (previousPetTypeIdRef.current === petTypeId) return;
    previousPetTypeIdRef.current = petTypeId;
    setValue('petBreedId', DEFAULT_OPTION_VALUE);
  }, [petTypeId, setValue]);

  // Clear the stale End Date error once Start Date changes.
  const startDate = watch('startDate');
  const isInitialStartDate = useRef(true);
  useEffect(() => {
    if (isInitialStartDate.current) {
      isInitialStartDate.current = false;
      return;
    }
    trigger('endDate');
  }, [startDate, trigger]);

  const sections = useMemo(() => {
    const fields = buildPetFields({
      petTypeOptions,
      breedOptions,
      requiresWeight,
      disabled: areFieldsDisabled,
      hideDates: isApplicantSubject,
      // A pet being added is current, so it has no end date to record.
      hideEndDate: isNewPet,
      lockedHelperText: isLockedByOpenCerts ? openCertHelperText : null,
      householdMoveInDay,
      formatMessage,
      getValues,
    });
    return resolvedSections.map((section) => ({ ...section, fields }));
  }, [
    resolvedSections,
    petTypeOptions,
    breedOptions,
    requiresWeight,
    areFieldsDisabled,
    isApplicantSubject,
    isNewPet,
    isLockedByOpenCerts,
    openCertHelperText,
    householdMoveInDay,
    formatMessage,
    getValues,
  ]);

  // Disable lease-charge queries for applicant pets.
  const { petCharges, petRentConfigured, isLoadingPetCharges } = usePetCharges({
    propertyId: isApplicantSubject ? undefined : propertyId,
    organizationId,
  });

  const petFeeModal = usePetFeeModal({
    // The modal charges one pet, so each fee count has a maximum of one.
    newPetsToCharge: [{ name: getValues()?.name ?? '' }],
    petCharges,
    isLoadingPetCharges,
  });
  const {
    open: isFeeModalOpen,
    handleOpen: handleOpenFeeModal,
    handleClose: closeFeeModal,
  } = petFeeModal;
  const openFeeModal = useCallback(
    () => handleOpenFeeModal({}),
    [handleOpenFeeModal],
  );

  const householdPetMutation = useHouseholdPetMutation({
    organizationId,
    propertyId,
    residentId,
  });

  // Deletion is a hard delete of the Applicant behind the pet, for applicant
  // and resident pets alike.
  const hardDeletePetMutation = useHardDeletePetMutation({
    organizationId,
    propertyId,
  });

  const applicantPetMutation = useCreateApplicantHouseholdMemberMutation({
    organizationId,
    propertyId,
    applicationId: applicationId ?? '',
    options: {},
  });

  // `updateApplicants` requires and validates the complete adult list.
  const existingAdults = useMemo(
    () => (isApplicantSubject ? getExistingApplicantAdults(applicants) : []),
    [isApplicantSubject, applicants],
  );

  /**
   * Blocks duplicate writes through request completion and cache invalidation.
   * A ref blocks clicks that occur within one React batch.
   */
  const inFlightRef = useRef(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const isMutating =
    householdPetMutation.isLoading ||
    applicantPetMutation.isLoading ||
    hardDeletePetMutation.isLoading ||
    isFinishing;

  // Add operations wait for charges because they can require the fee modal.
  // Saving also waits on the move-in date, so a Start Date is never accepted
  // before the rule that bounds it is known.
  const isBusy =
    isMutating ||
    isLoadingPetTypes ||
    isLoadingResident ||
    (isNewPet && isLoadingPetCharges);

  /** Notifies, refreshes all data sources, and closes the modal in that order. */
  const finishSuccessfulMutation = useCallback(
    async (response, successMessage) => {
      setIsFinishing(true);
      // Missing transaction setup does not fail the pet operation.
      notifyMissingTransactionTypes(response?.transactionTypesNotSetup);

      // Refresh applicant data through `onSaveSuccess`.
      if (!isApplicantSubject) {
        await invalidateOccupantInfoQueries(queryClient, {
          organizationId,
          propertyId,
          residentId,
        });
      }

      onSaveSuccess?.();

      toastr.success(formatMessage(messages.saveSuccessTitle), successMessage);

      setConfirmationOpen(false);
      closeFeeModal();
      onClose();
    },
    [
      queryClient,
      organizationId,
      propertyId,
      residentId,
      isApplicantSubject,
      onSaveSuccess,
      onClose,
      closeFeeModal,
      formatMessage,
    ],
  );

  // Preserve the form after an error so the user can retry.
  const handleMutationError = useCallback(
    (error, fallbackMessage) => {
      toastr.error(
        formatMessage(messages.saveErrorTitle),
        extractErrorMessage(error) || fallbackMessage,
      );
    },
    [formatMessage],
  );

  const submitAdd = useCallback(
    async (selectedFees: Array<SelectedFee>) => {
      // Prevent duplicate pet charges.
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const values = getValues();
      const payload = selectedFees.length
        ? buildAddPetWithFeesPayload(values, selectedFees)
        : buildAddPetPayload(values);
      try {
        const response = await householdPetMutation.mutateAsync(payload);
        await finishSuccessfulMutation(
          response,
          formatMessage(messages.addSuccessMessage),
        );
      } catch (error) {
        // Keep the completed Pet form available for correction.
        closeFeeModal();
        setIsFinishing(false);
        handleMutationError(error, formatMessage(messages.saveErrorMessage));
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      getValues,
      householdPetMutation,
      finishSuccessfulMutation,
      formatMessage,
      handleMutationError,
      closeFeeModal,
    ],
  );

  /** Applies shared completion and error handling to update and delete. */
  const submitPetWrite = useCallback(
    async (mutation, payload, successMessage, errorMessage) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const response = await mutation.mutateAsync(payload);
        await finishSuccessfulMutation(response, successMessage);
      } catch (error) {
        setIsFinishing(false);
        handleMutationError(error, errorMessage);
      } finally {
        inFlightRef.current = false;
      }
    },
    [finishSuccessfulMutation, handleMutationError],
  );

  const submitUpdate = useCallback(
    (mutation, payload) =>
      submitPetWrite(
        mutation,
        payload,
        formatMessage(messages.updateSuccessMessage),
        formatMessage(messages.saveErrorMessage),
      ),
    [submitPetWrite, formatMessage],
  );

  /** Opens the fee modal only when an add operation has configured charges. */
  const handleSave = useCallback(async () => {
    // Defense in depth: the buttons are disabled, but neither write path may
    // depend on the UI alone for a rule the server does not enforce.
    if (isLockedByOpenCerts || isUnsupportedPet) return;
    const isValid = await trigger();
    if (!isValid) return;

    // Submit applicant pet changes without lease charges.
    if (isApplicantSubject) {
      await submitPetWrite(
        applicantPetMutation,
        buildApplicantPetPayload({ pet, values: getValues(), existingAdults }),
        formatMessage(
          isNewPet ? messages.addSuccessMessage : messages.updateSuccessMessage,
        ),
        formatMessage(messages.saveErrorMessage),
      );
      return;
    }

    // Update operations do not depend on pet-charge data.
    if (isResidentPet && pet) {
      await submitUpdate(
        householdPetMutation,
        buildUpdateResidentPetPayload(pet, getValues()),
      );
      return;
    }

    // Do not add a pet before charge data is available.
    if (isLoadingPetCharges) return;

    const shouldOpenFeeModal =
      Boolean(automatedLeaseChargesP1) &&
      (petCharges.length > 0 || petRentConfigured);

    if (shouldOpenFeeModal) {
      openFeeModal();
      return;
    }

    await submitAdd([]);
  }, [
    trigger,
    isLockedByOpenCerts,
    isUnsupportedPet,
    isApplicantSubject,
    isNewPet,
    applicantPetMutation,
    existingAdults,
    submitPetWrite,
    isResidentPet,
    pet,
    householdPetMutation,
    submitUpdate,
    getValues,
    formatMessage,
    automatedLeaseChargesP1,
    isLoadingPetCharges,
    petCharges,
    petRentConfigured,
    openFeeModal,
    submitAdd,
  ]);

  // Submit the pet and selected fees in one atomic request.
  const handleFeeModalConfirm = useCallback(async () => {
    const selectedFees = buildSelectedFees(
      petFeeModal.reactHookFormProps.getValues(),
    );
    await submitAdd(selectedFees);
  }, [petFeeModal.reactHookFormProps, submitAdd]);

  // Fee-modal dismissal preserves the Pet form without saving.
  const handleFeeModalCancel = useCallback(() => {
    closeFeeModal();
  }, [closeFeeModal]);

  const handleDeleteClick = useCallback(() => setConfirmationOpen(true), []);

  // Delete cancellation closes both confirmation and Pet modals.
  const handleDeleteCancel = useCallback(() => {
    setConfirmationOpen(false);
    onClose();
  }, [onClose]);

  // A pet without an Applicant ID has no record the delete route can address.
  // The open-cert lock is applied at the button, which stays visible and says
  // why it is inert.
  const canDeletePet = Boolean(pet && canDelete && pet.applicantId);

  /** Permanently deletes the pet through its Applicant record. */
  const handleDeleteConfirm = useCallback(async () => {
    if (!pet?.applicantId || !canDeletePet || isLockedByOpenCerts) return;

    await submitPetWrite(
      hardDeletePetMutation,
      pet.applicantId,
      formatMessage(messages.deleteSuccessMessage),
      formatMessage(messages.deleteErrorMessage),
    );
  }, [
    pet,
    canDeletePet,
    isLockedByOpenCerts,
    hardDeletePetMutation,
    submitPetWrite,
    formatMessage,
  ]);

  const title = isNewPet
    ? formatMessage(messages.addPetTitle)
    : pet?.name || formatMessage(messages.addPetTitle);

  return {
    intl,
    formatMessage,
    title,
    isNewPet,
    isResidentPet,
    isUnsupportedPet,
    isMutating,
    // Save also waits for reference and charge data.
    isBusy,
    isLockedByOpenCerts,
    openCertHelperText,
    unsupportedPetText: formatMessage(messages.unsupportedPetBanner),
    isLoading: isLoadingPetTypes,
    ReactHookFormProps,
    sections,
    confirmationOpen,
    canDeletePet,
    handleSave,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    // Fee modal state and actions.
    petFeeModal,
    isFeeModalOpen,
    handleFeeModalConfirm,
    handleFeeModalCancel,
    petRentConfigured,
    leaseMonthlyPetRent,
    // Show recurring-rent information only for add operations.
    showPetRentWarning:
      isNewPet && Boolean(automatedLeaseChargesP1) && petRentConfigured,
    petNameForCharges: getValues()?.name ?? '',
  };
};

export default usePetModal;
